/**
 * Assistant Debug Modal
 *
 * Displays detailed debug information about governed tool execution.
 * Only visible to superadmin users.
 */

import { IconBug, IconCheck, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { Modal } from "~/components/_design-system/Modal";
import { InfoAlert } from "~/components/Alert";
import { useAuthorization } from "~/contexts/authorizationProvider";
import type { DebugPayload, DebugStage } from "~/shared/ai/assistant-event-contract";
import { GRAY_PALETTE } from "~/styles/palettes";
import { Accordion, ActionIcon, Badge, Code, Divider, Flex, Paper, Text, Tooltip } from "~/ui";

interface AssistantDebugModalProps {
  /** Debug payloads from governed tool executions */
  debugPayloads: DebugPayload[];
  /** Clear all debug payloads */
  onClear: () => void;
}

/**
 * Formats a JSON value for display, handling BigInt and other special types.
 */
const formatJson = (value: unknown): string => {
  try {
    const replacer = (_key: string, val: unknown) => {
      if (typeof val === "bigint") {
        return val.toString();
      }
      return val;
    };
    return JSON.stringify(value, replacer, 2);
  } catch {
    return String(value);
  }
};

/**
 * Single debug stage display.
 */
const DebugStageRow = ({ stage, index }: { stage: DebugStage; index: number }) => {
  const statusColor = stage.status === "success" ? "green" : stage.status === "error" ? "red" : "gray";
  const StatusIcon = stage.status === "success" ? IconCheck : stage.status === "error" ? IconX : null;

  return (
    <Paper p="sm" withBorder radius="sm" mb="sm" bg={stage.status === "error" ? "red.0" : undefined}>
      <Flex align="center" gap="sm" mb="xs">
        <Badge size="sm" color="gray" variant="light">
          {index + 1}
        </Badge>
        <Text fw={600} size="sm">
          {stage.name}
        </Text>
        <Badge size="sm" color={statusColor} leftSection={StatusIcon ? <StatusIcon size={12} /> : null}>
          {stage.status}
        </Badge>
        <Text size="xs" c="dimmed">
          {stage.durationMs}ms
        </Text>
      </Flex>
      {stage.input !== undefined && stage.input !== null && (
        <Accordion variant="contained" radius="sm" mb="xs">
          <Accordion.Item value="input">
            <Accordion.Control>
              <Text size="xs" fw={500}>
                Input
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Code block style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 11 }}>
                {formatJson(stage.input)}
              </Code>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}
      {stage.output !== undefined && stage.output !== null && (
        <Accordion variant="contained" radius="sm" mb="xs">
          <Accordion.Item value="output">
            <Accordion.Control>
              <Text size="xs" fw={500}>
                Output
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Code block style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 11 }}>
                {formatJson(stage.output)}
              </Code>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}
      {stage.error !== undefined && stage.error !== null && (
        <Paper p="xs" bg="red.1" radius="sm">
          <Text size="xs" fw={500} c="red" mb="xs">
            Error
          </Text>
          <Code block style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 11 }}>
            {stage.error}
          </Code>
        </Paper>
      )}
    </Paper>
  );
};

/**
 * Single debug payload (one governed tool execution).
 */
const DebugPayloadSection = ({ payload, index }: { payload: DebugPayload; index: number }) => {
  return (
    <Accordion.Item value={`execution-${index}`}>
      <Accordion.Control>
        <Flex align="center" gap="sm">
          <Text fw={600} size="sm">
            Execution {index + 1}
          </Text>
          <Badge size="xs" color="gray" variant="light">
            {payload.totalDurationMs}ms
          </Badge>
          <Badge size="xs" color="blue" variant="light">
            {payload.stages.length} stages
          </Badge>
        </Flex>
      </Accordion.Control>
      <Accordion.Panel>
        <Text size="xs" c="dimmed" mb="sm">
          {new Date(payload.timestamp).toLocaleString()}
        </Text>
        {payload.stages.map((stage, stageIndex) => (
          <DebugStageRow key={stageIndex} stage={stage} index={stageIndex} />
        ))}
      </Accordion.Panel>
    </Accordion.Item>
  );
};

/**
 * Assistant Debug Modal.
 * Shows detailed execution trace for superadmin debugging.
 */
export const AssistantDebugModal = ({ debugPayloads, onClear }: AssistantDebugModalProps) => {
  const { isSuperAdmin } = useAuthorization();
  const [isOpen, setIsOpen] = useState(false);

  // Only render for superadmins
  if (!isSuperAdmin) {
    return null;
  }

  const hasDebugData = debugPayloads.length > 0;

  return (
    <>
      <Tooltip label={hasDebugData ? `Debug (${debugPayloads.length} executions)` : "Debug (no data yet)"}>
        <ActionIcon color="gray" radius="md" variant="subtle" onClick={() => setIsOpen(true)} style={{ opacity: hasDebugData ? 1 : 0.5 }}>
          <IconBug size={16} />
        </ActionIcon>
      </Tooltip>
      <Modal active={isOpen} size="xl" title="Assistant Debug" onClose={() => setIsOpen(false)}>
        <Flex direction="column" gap="md">
          <Flex justify="space-between" align="flex-start">
            <InfoAlert>
              This modal is only visible to ShelfCycle Superadmin users. It shows the execution trace for debugging report failures.
            </InfoAlert>
            {hasDebugData && (
              <ActionIcon variant="subtle" color="gray" onClick={onClear} title="Clear debug data" ml="sm">
                <IconX size={16} />
              </ActionIcon>
            )}
          </Flex>
          {!hasDebugData ? (
            <Flex direction="column" align="center" py="xl" gap="sm">
              <IconBug size={48} stroke={1} color={GRAY_PALETTE[5]} />
              <Text c="dimmed">No debug data yet. Submit a question with debug mode enabled.</Text>
              <Text size="xs" c="dimmed">
                Debug mode is automatically enabled for superadmins.
              </Text>
            </Flex>
          ) : (
            <>
              <Divider label="Execution Trace" labelPosition="center" />
              <Accordion variant="separated" radius="md" defaultValue="execution-0">
                {debugPayloads.map((payload, index) => (
                  <DebugPayloadSection key={payload.timestamp} payload={payload} index={index} />
                ))}
              </Accordion>
            </>
          )}
        </Flex>
      </Modal>
    </>
  );
};
